import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        console.error(e);
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center space-y-6 py-20" data-testid="success-page">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-white" size={48} />
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
              Confirming Your Order
            </h1>
            <p className="text-zinc-400 text-xs tracking-widest uppercase">
              Hold tight — we're verifying payment with Stripe.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={56} />
            <h1 className="font-heading text-5xl font-black uppercase tracking-tight leading-none">
              Welcome to the <span className="metallic-text">Legacy</span>
            </h1>
            <p className="text-zinc-400 text-sm tracking-wide max-w-md mx-auto">
              Your order is confirmed. Your gear is being prepared with the
              same discipline that built this brand.
            </p>
            {order && (
              <div
                className="border border-zinc-800 p-5 text-left space-y-2 bg-zinc-950"
                data-testid="success-order-summary"
              >
                <div className="flex justify-between text-xs uppercase tracking-widest text-zinc-500">
                  <span>Total Paid</span>
                  <span className="text-white font-bold">
                    ${(order.amount_total / 100).toFixed(2)}{" "}
                    {order.currency?.toUpperCase()}
                  </span>
                </div>
                {(order.items || []).map((i, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between text-xs text-zinc-400"
                  >
                    <span>
                      {i.name} × {i.quantity}
                    </span>
                    <span>${(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            <Link
              to="/"
              className="inline-block bg-white text-black font-heading font-bold text-xs tracking-widest uppercase px-8 py-4 hover:bg-zinc-300"
              data-testid="success-home-btn"
            >
              Continue Shopping
            </Link>
          </>
        )}

        {(state === "failed" || state === "expired") && (
          <>
            <XCircle className="mx-auto text-red-500" size={56} />
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
              Payment Not Confirmed
            </h1>
            <p className="text-zinc-400 text-xs tracking-widest uppercase">
              {state === "expired"
                ? "Your checkout session expired. Please try again."
                : "We couldn't confirm payment status. If you were charged, contact support."}
            </p>
            <Link
              to="/"
              className="inline-block border border-zinc-700 text-white font-heading font-bold text-xs tracking-widest uppercase px-8 py-4 hover:bg-zinc-900"
              data-testid="success-retry-btn"
            >
              Back to Shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Success;
